import React, { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { ChoiceDialog, ChoiceOption } from "./ChoiceDialog";

/**
 * The "question card" shown when adding a project to an organisation.
 *
 * Step 1 asks which kind of add this is:
 *   • Create a new project  → hands off to the shared Create Project wizard
 *   • Add an existing one   → step 2, a searchable picker
 *
 * Only projects with no organisation are offered, because a project may
 * belong to at most one.
 */

const PlusIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
);

const AddProjectQuestionModal = ({
  open,
  onClose,
  onCreateNew,
  onAddExisting,
  unassignedProjects = [],
  isLoadingProjects,
  isAdding,
  error,
  // "existing" jumps straight to the picker — used by the empty-state
  // "Link Existing Project" card. Defaults to the chooser.
  initialMode = "ask",
}) => {
  const [mode, setMode] = useState(initialMode); // "ask" | "existing"
  const [choice, setChoice] = useState("new"); // "new" | "existing"
  const [search, setSearch] = useState("");

  // Each opening starts at the step the caller asked for.
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  if (!open) return null;

  const close = () => {
    setMode("ask");
    setChoice("new");
    setSearch("");
    onClose();
  };

  // Continue does exactly what clicking a card used to do.
  const proceed = () => {
    if (choice === "new") {
      close();
      onCreateNew();
    } else {
      setMode("existing");
    }
  };

  const filtered = (unassignedProjects || []).filter((p) =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const errorBox = error && (
    <div className="mb-4 rounded-[10px] border border-red-200 bg-red-50 p-3 text-sm text-red-600">
      {error}
    </div>
  );

  if (mode === "ask") {
    return (
      <ChoiceDialog
        icon={<PlusIcon />}
        title="Add a Project"
        subtitle="Would you like to build a new project from scratch or link an existing one?"
        onClose={close}
        footerHint={
          <span className="flex items-center gap-2">
            <kbd className="rounded-[5px] border border-[#D0D5DD] bg-white px-1.5 py-0.5 text-[10px] font-medium text-[#667085]">
              Esc
            </kbd>
            to dismiss
          </span>
        }
        primaryLabel="Continue"
        onPrimary={proceed}
      >
        {errorBox}
        <div role="radiogroup" className="space-y-3">
          <ChoiceOption
            selected={choice === "new"}
            onSelect={() => setChoice("new")}
            icon={<PlusIcon />}
            title="Create New Project"
            tag="Recommended"
            description="Opens the full setup wizard to configure the project, its team members, dates and details."
          />
          <ChoiceOption
            selected={choice === "existing"}
            onSelect={() => setChoice("existing")}
            icon={<CopyIcon />}
            title="Add Existing Project"
            description="Move a project that isn't in any organisation yet directly into this one."
          />
        </div>
      </ChoiceDialog>
    );
  }

  return (
    <ChoiceDialog
      icon={<CopyIcon />}
      title="Add Existing Project"
      subtitle="Only projects that aren't already in an organisation are listed."
      onClose={close}
    >
      {errorBox}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects"
          className="h-[42px] w-full rounded-[10px] border border-[#D0D5DD] bg-white pl-10 pr-3 text-sm outline-none focus:border-[#4F46E5]"
        />
      </div>

      <div className="max-h-[280px] overflow-y-auto rounded-[12px] border border-[#E4E7EC] bg-white">
        {isLoadingProjects ? (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-[#98A2B3]">
            {(unassignedProjects || []).length === 0
              ? "Every project already belongs to an organisation."
              : "No projects match that search."}
          </p>
        ) : (
          filtered.map((p) => (
            <button
              key={p._id}
              disabled={isAdding}
              onClick={() => onAddExisting(p._id)}
              className="flex w-full items-center justify-between border-b border-[#F2F4F7] px-4 py-3 text-left last:border-b-0 hover:bg-[#F9FAFB] disabled:opacity-60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[#111827]">
                  {p.name}
                </p>
                <p className="truncate text-[12px] text-[#98A2B3]">
                  {p.description || "—"}
                </p>
              </div>
              <span className="ml-3 shrink-0 rounded-[6px] bg-[#EEF0FF] px-2.5 py-1 text-xs font-semibold text-[#4F46E5]">
                Add
              </span>
            </button>
          ))
        )}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          onClick={() => setMode("ask")}
          className="h-10 rounded-[10px] border border-[#D0D5DD] bg-white px-5 text-sm font-medium text-[#344054] hover:bg-[#F9FAFB]"
        >
          Back
        </button>
      </div>
    </ChoiceDialog>
  );
};

export default AddProjectQuestionModal;
